import { planningPeriod } from "./planning-period";
import { projectSnapshots } from "./project-portfolio";

export function dashboardWeek(anchor = new Date().toISOString().slice(0, 10)) {
  return planningPeriod("Woche", anchor);
}

export function dashboardEntries(entries = [], employeeId, period) {
  return entries.filter(entry => entry.mitarbeiterId === employeeId && entry.datum >= period.from && entry.datum <= period.to);
}

export function dashboardCapacity(capacity = [], employeeId, period) {
  const employee = capacity.find(item => item.mitarbeiterId === employeeId);
  return employee?.wochen?.find(week => week.von === period.from) || null;
}

const DAY = 86400000;
const iso = date => date.toISOString().slice(0, 10);
const addDays = (value, days) => iso(new Date(new Date(`${value}T00:00:00Z`).valueOf() + days * DAY));

/** The four sections and wording are the inventory of PROTO:viewDashboardMy. */
export function personalDashboardPeriods(anchor, today = new Date().toISOString().slice(0, 10)) {
  const selected = dashboardWeek(anchor);
  const current = dashboardWeek(today);
  const reference = selected.from === current.from ? today : selected.from;
  const tomorrow = addDays(reference, 1);
  const restFrom = addDays(reference, 2);
  const nextFrom = addDays(selected.from, 7);
  const range = (from, to) => from <= to ? { from, to } : null;
  return [
    { key:"today", label:"Heute", period:range(reference, reference) },
    { key:"tomorrow", label:"Morgen", period:tomorrow <= selected.to ? range(tomorrow, tomorrow) : null },
    { key:"rest", label:"Rest der Woche", period:range(restFrom, selected.to) },
    { key:"next", label:"Nächste Woche", period:range(nextFrom, addDays(nextFrom, 6)) }
  ];
}

export function entriesForDashboardPeriod(entries = [], employeeId, section) {
  if (!section.period) return [];
  return entries.filter(entry => entry.mitarbeiterId === employeeId && entry.datum >= section.period.from && entry.datum <= section.period.to)
    .sort((a, b) => a.datum.localeCompare(b.datum));
}

export function activeMandates(mandates = []) {
  return mandates.filter(mandate => mandate.status === "aktiv");
}

export function mandatesWithoutPlanning(mandates = [], entries = [], planningYear) {
  const snapshots = projectSnapshots(entries, new Date(), {}, planningYear);
  return activeMandates(mandates)
    .filter(mandate => (snapshots[mandate.id]?.plannedHours || 0) === 0)
    .sort((a, b) => a.name.localeCompare(b.name, "de-CH"));
}

export function upcomingExternalAppointments(entries = [], mandates = [], employees = [], today = new Date().toISOString().slice(0, 10)) {
  const mandateById = new Map(activeMandates(mandates).map(mandate => [mandate.id, mandate]));
  const employeeById = new Map(employees.map(employee => [employee.mitarbeiterId || employee.id, employee]));
  const day = new Date(`${today}T00:00:00Z`);
  const monday = new Date(day);
  monday.setUTCDate(day.getUTCDate() - (day.getUTCDay() || 7) + 1);
  const nextSunday = new Date(monday);
  nextSunday.setUTCDate(monday.getUTCDate() + 13);
  const through = nextSunday.toISOString().slice(0, 10);
  const groups = new Map();

  entries.filter(entry => entry.inhouseExtern === "Extern" && entry.datum >= today && entry.datum <= through && mandateById.has(entry.mandatId) && employeeById.has(entry.mitarbeiterId)).forEach(entry => {
    const key = `${entry.mandatId}:${entry.datum}`;
    const group = groups.get(key) || { mandatId:entry.mandatId, datum:entry.datum, mandate:mandateById.get(entry.mandatId), employees:[] };
    const employee = employeeById.get(entry.mitarbeiterId);
    if (employee && !group.employees.some(item => (item.mitarbeiterId || item.id) === entry.mitarbeiterId)) group.employees.push(employee);
    groups.set(key, group);
  });

  return [...groups.values()].sort((a, b) => a.datum.localeCompare(b.datum) || a.mandate.name.localeCompare(b.mandate.name, "de-CH"));
}


export function criticalDeadlines(milestones = [], mandates = [], today = new Date().toISOString().slice(0, 10), days = 10) {
  const mandateById = new Map(activeMandates(mandates).map(mandate => [mandate.id, mandate]));
  const end = new Date(`${today}T00:00:00Z`);
  end.setUTCDate(end.getUTCDate() + days);
  const through = end.toISOString().slice(0, 10);
  return milestones
    .filter(item => item.kritisch && item.datum >= today && item.datum <= through && mandateById.has(item.mandatId))
    .map(item => ({...item, mandate:mandateById.get(item.mandatId)}))
    .sort((a,b) => a.datum.localeCompare(b.datum) || a.bezeichnung.localeCompare(b.bezeichnung));
}

export const DASHBOARD_PHASES = ["ZR", "SR", "Inv", "Kons"];

/** Resolve attention records only through active, readable mandates. Orphans are reported, never rendered. */
export function teamAttention({ mandates = [], entries = [], milestones = [], confirmations = [] } = {}) {
  const active = activeMandates(mandates);
  const mandateById = new Map(active.map(item => [item.id, item]));
  const validEntries = entries.filter(item => mandateById.has(item.mandatId));
  const hours = new Map();
  validEntries.forEach(item => hours.set(item.mandatId, (hours.get(item.mandatId) || 0) + Number(item.stunden || 0)));
  const budgetOverruns = active.filter(item => (hours.get(item.id) || 0) > Number(item.budget || 0)).map(item => ({ mandate:item, geplant:hours.get(item.id), budget:Number(item.budget) }));
  const phases = Object.fromEntries(DASHBOARD_PHASES.map(phase => [phase, active.filter(mandate => validEntries.some(item => item.mandatId === mandate.id && item.phaseId === phase))]));
  const reviewsReports = milestones.filter(item => mandateById.has(item.mandatId) && /(review|bericht|report)/i.test(item.bezeichnung || "")).map(item => ({ ...item, mandate:mandateById.get(item.mandatId) })).sort((a,b) => a.datum.localeCompare(b.datum));
  const isOpen = item => !/(erhalten|abgeschlossen|erledigt)/i.test(`${item.bestaetigungsstatusId || item.status || ""}`);
  const openConfirmations = confirmations.filter(item => mandateById.has(item.mandatId) && isOpen(item)).map(item => ({ ...item, mandate:mandateById.get(item.mandatId) })).sort((a,b) => (a.faelligAm || "9999").localeCompare(b.faelligAm || "9999"));
  const knownMandates = new Set(mandates.map(item => item.id));
  const unresolvedCount = [...entries, ...milestones, ...confirmations].filter(item => item.mandatId && !knownMandates.has(item.mandatId)).length;
  return { budgetOverruns, phases, reviewsReports, openConfirmations, unresolvedCount };
}

export function filterTeamCapacity(capacity = [], entries = [], filters = {}, mandates = []) {
  const groupMandates = filters.group ? new Set(mandates.filter(item => item.gruppe === filters.group && item.status === "aktiv").map(item => item.id)) : null;
  const matchingEmployees = filters.mandatId || groupMandates ? new Set(entries.filter(item => (!filters.mandatId || item.mandatId === filters.mandatId) && (!groupMandates || groupMandates.has(item.mandatId))).map(item => item.mitarbeiterId)) : null;
  return capacity.filter(item => (!filters.employeeId || item.mitarbeiterId === filters.employeeId) && (!matchingEmployees || matchingEmployees.has(item.mitarbeiterId)));
}
